<template>
    <div>
        <div v-if="resolvedVideo && resolvedVideo.processed">
            <template v-if="debugMode">
                <a :href="`/nova/resources/videos/${resolvedVideo?.id}`" target="_blank"> #{{ resolvedVideo?.id }} </a>
            </template>
            <div
                v-if="resolvedVideo.thumbnailUrl"
                class="video-container mx-auto my-1"
                :style="{ width: maxWidth, height: maxHeight }"
                @click.stop="openVideo"
            >
                <v-img
                    :max-width="maxWidth"
                    :max-height="maxHeight"
                    :src="resolvedVideo?.thumbnailUrl"
                    style="border: 2px solid #546e7a; border-radius: 5px"
                />
                <div v-if="maxWidth >= 50" class="play-button">
                    <v-icon style="color: white">mdi-play-circle-outline</v-icon>
                </div>
            </div>
            <v-btn v-else color="primary" @click.stop="openVideo"> Play </v-btn>
        </div>
        <div v-else-if="videoIsProcessing">
            <v-progress-circular
                v-if="!showDeleteVideoButton"
                :size="30"
                color="primary"
                :value="progress"
                :indeterminate="progress === 0"
                @click.stop="
                    routine.userCanModifyVideo($page.props.auth.user, result) ? (showDeleteVideoButton = true) : null
                "
            />
            <v-btn
                v-if="routine.userCanModifyVideo($page.props.auth.user, result) && showDeleteVideoButton"
                color="red darken-1"
                icon
                @click.stop="confirmRemove = true"
            >
                <v-icon>mdi-delete</v-icon>
            </v-btn>
        </div>
        <div v-else-if="resolvedVideo?.processing_failed_at">
            <v-btn
                v-if="!showDeleteVideoButton"
                color="red"
                icon
                @click.stop="
                    routine.userCanModifyVideo($page.props.auth.user, result) ? (showDeleteVideoButton = true) : null
                "
            >
                <v-icon>mdi-alert-circle</v-icon>
            </v-btn>
            <v-btn
                v-if="routine.userCanModifyVideo($page.props.auth.user, result) && showDeleteVideoButton"
                color="red darken-1"
                icon
                @click.stop="confirmRemove = true"
            >
                <v-icon>mdi-delete</v-icon>
            </v-btn>
        </div>
        <div v-else>
            <v-btn v-if="routine.userCanModifyVideo(user, result)" icon @click="selectFile(routine.id)">
                <v-icon>mdi-upload</v-icon>
            </v-btn>
            <input
                :ref="`routine-${routine.id}`"
                type="file"
                accept="video/*"
                style="display: none"
                @change="event => createVideo(event)"
            />
        </div>

        <v-snackbar v-model="encounteredError">
            {{ errorMessage }}

            <template #action="{ attrs }">
                <v-btn color="red darken-1" text v-bind="attrs" @click="encounteredError = false"> Close </v-btn>
            </template>
        </v-snackbar>

        <v-snackbar v-model="uploadComplete" :timeout="3000">
            Your video has been uploaded and is ready to view.

            <template #action="{ attrs }">
                <v-btn color="green darken-1" text v-bind="attrs" @click="uploadComplete = false"> Close </v-btn>
            </template>
        </v-snackbar>

        <v-snackbar v-model="uploadFailed" :timeout="3000">
            Your video failed to upload. Please try again.

            <template #action="{ attrs }">
                <v-btn color="red darken-1" text v-bind="attrs" @click="uploadFailed = false"> Close </v-btn>
            </template>
        </v-snackbar>

        <v-bottom-sheet v-model="showUploadProgress" persistent>
            <v-sheet class="text-center">
                <div class="py-8">
                    Upload Progress: {{ uploadProgress }}%
                    <v-progress-linear :value="uploadProgress" color="blue" />
                </div>
            </v-sheet>
        </v-bottom-sheet>

        <v-dialog v-model="confirmRemove" max-width="500px">
            <v-card>
                <v-card-title> Remove Video </v-card-title>
                <v-card-text> Are you sure you wish to remove this video? </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="confirmRemove = false"> Cancel </v-btn>
                    <v-btn color="red darken-1" text :loading="removingVideo" @click="removeVideo"> Remove </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
// TODO: Refactor this component to take direct props instead of inferring them from passed objects
// The result will be more flexible so that it's not required for Vuex-ORM to have the relationships pre loaded.
import axios from 'axios';
import Result from '@/Models/Result.js';
import Score from '@/Models/BaseScore.js';
import Video from '@/Models/Video.js';
import { eventFromProscore } from '@/helpers.js';
import ResultCategory from '@/Models/ResultCategory.js';
import Athlete from '@/Models/Athlete.js';
import User from '@/Models/User.js';

export default {
    name: 'ScorsyncVideo',

    props: {
        routine: {
            type: Score,
            required: true,
        },

        result: {
            type: Result,
            required: true,
        },

        resultCategory: {
            type: ResultCategory,
            required: false,
            default: null,
        },

        video: {
            type: Video,
            required: false,
            default: null,
        },

        athlete: {
            type: Athlete,
            required: false,
            default: null,
        },

        round: {
            type: String,
            required: true,
        },

        size: {
            type: String,
            required: false,
            default: null,
        },

        meetId: {
            type: Number,
            required: false,
            default: null,
        },
    },

    data: () => ({
        uploadComplete: false,
        uploadFailed: false,
        confirmRemove: false,
        removingVideo: false,
        reportReason: '',
        reportConfirmation: false,
        encounteredError: false,
        errorMessage: '',
        progress: 0,
        progressChecker: null,
        progressChecks: 0,
        showDeleteVideoButton: false,
        uploadProgress: 0,
        showUploadProgress: false,
    }),

    computed: {
        user() {
            if (!this.$page.props.auth.user) {
                return null;
            }

            return User.query().with('affiliations.affiliate').find(this.$page.props.auth.user.id);
        },

        resolvedVideo() {
            if (this.video) {
                return this.video;
            }

            return this.routine.video;
        },

        videoIsProcessing() {
            return this.resolvedVideo && !this.resolvedVideo.processed && !this.resolvedVideo.processing_failed_at;
        },

        maxWidth() {
            return this.size || 50;
        },

        maxHeight() {
            return this.size || 50;
        },

        debugMode() {
            return this.$store.state.debugMode;
        },
    },

    created() {
        if (this.videoIsProcessing) {
            this.progressChecker = setInterval(async () => {
                await this.checkProgress();

                if (this.progress === 100 || this.progressChecks > 120) {
                    clearInterval(this.progressChecker);
                }
            }, 20 * 1000);
        }
    },

    beforeDestroy() {
        clearInterval(this.progressChecker);
    },

    methods: {
        eventFromProscore,

        async createVideo(event) {
            const extension = event.target.files[0].name.split('.').pop();

            try {
                const response = await Video.api().post('/api/videos', {
                    title: 'Video', // Change
                    description: 'Video', // Change
                    routine_id: this.routine.id,
                    routine_type: Score.routineModel(this.resultCategory.event),
                    num: this.routine.num,
                    extension,
                });

                const video = response.entities.videos[0];

                return this.uploadVideo(video, event.target.files[0]);
            } catch (e) {
                this.captureErrorMessage(e);
                this.$refs[`routine-${this.routine.id}`].value = null;

                return null;
            }
        },

        async uploadVideo(video, file) {
            const presignedUrl = await this.generatePresignedUrl(video, file);

            await this.uploadToPresignedUrl(presignedUrl, file);

            const formData = new FormData();
            formData.append('path', video.video_filename);
            formData.append('uuid', video.uuid);

            try {
                this.showUploadProgress = true; // Show the upload progress indicator
                await axios.post('/api/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Accept: 'application/json',
                    },
                });

                this.showUploadProgress = false; // Hide the upload progress indicator once upload is complete

                this.$echo.channel(`video-${video.uuid}`).listen('VideoProcessed', e => {
                    Video.insert({ data: e.video });
                    this.uploadComplete = true;
                });

                this.$echo.channel(`video-${video.uuid}`).listen('VideoFailed', e => {
                    Video.insert({ data: e.video });
                    this.uploadFailed = true;
                });
            } catch (e) {
                this.captureErrorMessage(e);
                this.showUploadProgress = false; // Ensure the indicator is hidden on error

                if (this.$refs[`routine-${this.routine.id}`]) {
                    this.$refs[`routine-${this.routine.id}`].value = null;
                }
            }
        },

        async uploadToPresignedUrl(presignedUrl, file) {
            this.showUploadProgress = true;

            try {
                await axios.put(presignedUrl, file, {
                    headers: {
                        'Content-Type': file.type,
                    },
                    withCredentials: false,
                    onUploadProgress: progressEvent => {
                        const totalLength = progressEvent.lengthComputable
                            ? progressEvent.total
                            : progressEvent.target.getResponseHeader('content-length') ||
                              progressEvent.target.getResponseHeader('x-decompressed-content-length');

                        if (totalLength !== null) {
                            this.uploadProgress = Math.round((progressEvent.loaded * 100) / totalLength);
                        }
                    },
                });

                this.showUploadProgress = false;
            } catch (e) {
                this.captureErrorMessage(e);
                this.showUploadProgress = false; // Ensure the indicator is hidden on error

                if (this.$refs[`routine-${this.routine.id}`]) {
                    this.$refs[`routine-${this.routine.id}`].value = null;
                }

                await this.removeVideo();
            }
        },

        async generatePresignedUrl(video, file) {
            try {
                console.log({ fileName: video.video_filename, mimeType: file.type });

                const { data } = await axios.post('/api/generate-presigned-url', {
                    fileName: video.video_filename,
                    mimeType: file.type,
                });

                return data.presignedUrl.url;
            } catch (error) {
                console.error('Error generating pre-signed URL:', error);
                throw error;
            }
        },

        selectFile(routineId) {
            this.$refs[`routine-${routineId}`].click();
        },

        captureErrorMessage(e) {
            const { response } = e;

            this.encounteredError = true;

            if (e.status === 422) {
                this.errorMessage = response.data.errors.join('\n');
            } else {
                this.errorMessage = response?.data?.message || 'There was an issue uploading the video';
            }
        },

        download() {},

        async checkProgress() {
            this.progressChecks++;

            try {
                const { data } = await axios.get(`/api/videos/${this.resolvedVideo?.id}/status`);

                this.progress = data.data.progress_value;

                return this.progress;
            } catch (e) {
                if (e.response.status !== 400) {
                    throw e;
                }

                return null;
            }
        },

        openVideo() {
            this.$store.dispatch('VideoPlayer/openVideo', {
                resolvedVideo: this.resolvedVideo,
                result: this.result,
                athlete: this.athlete,
                routine: this.routine,
                round: this.round,
                resultCategory: this.resultCategory,
                meet_id: this.meetId || this.result.meet_id,
            });

            this.$emit('opened');
        },

        async removeVideo() {
            this.removingVideo = true;

            const video = this.resolvedVideo;

            await Video.api().delete(`/api/videos/${video.id}`);

            await video.$delete();

            this.removingVideo = false;

            this.confirmRemove = false;
            await this.closeVideo();

            this.showDeleteVideoButton = false;
            clearInterval(this.progressChecker);
        },

        closeVideo() {
            this.$store.dispatch('VideoPlayer/closeVideo');
        },
    },
};
</script>

<style lang="scss" scoped>
.video-container {
    position: relative;
    cursor: pointer;
}

.play-button {
    position: absolute;
    top: 13px;
    left: 13px;
    color: white;
}
</style>
